import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const blankForm = {
  name: '',
  company_name: '',
  title: '',
  phone: '',
  email: '',
  notes: '',
};

const ContactCard = ({ contact, onEdit, onDelete }) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`company-contact-card-${contact.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="font-semibold text-gray-900" data-testid={`company-contact-name-${contact.id}`}>{contact.name}</h3>
        <p className="text-sm text-gray-600" data-testid={`company-contact-company-${contact.id}`}>{contact.company_name}</p>
        {contact.title ? <p className="text-xs text-gray-500" data-testid={`company-contact-title-${contact.id}`}>{contact.title}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(contact)}
          className="px-2 py-1 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
          data-testid={`company-contact-edit-btn-${contact.id}`}
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(contact)}
          className="px-2 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50"
          data-testid={`company-contact-delete-btn-${contact.id}`}
        >
          Delete
        </button>
      </div>
    </div>
    <div className="mt-3 space-y-1">
      <p className="text-sm text-gray-700" data-testid={`company-contact-phone-${contact.id}`}>📞 {contact.phone || '—'}</p>
      <p className="text-sm text-gray-700" data-testid={`company-contact-email-${contact.id}`}>✉️ {contact.email || '—'}</p>
      {contact.notes ? <p className="text-xs text-gray-500" data-testid={`company-contact-notes-${contact.id}`}>{contact.notes}</p> : null}
    </div>
  </div>
);

export const CompanyContactsModule = ({ onBack, currentUser }) => {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(blankForm);
  const [editingContactId, setEditingContactId] = useState('');

  const canManage = ['admin', 'manager'].includes(currentUser?.rawRole || '');

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/company-contacts`);
      setContacts(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => [c.name, c.company_name, c.title, c.phone, c.email, c.notes]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(q)));
  }, [contacts, search]);

  const resetForm = () => {
    setForm(blankForm);
    setEditingContactId('');
    setError('');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) return;

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        company_name: form.company_name.trim(),
        title: form.title.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        notes: form.notes.trim(),
      };

      if (editingContactId) {
        await axios.put(`${API}/company-contacts/${editingContactId}`, payload);
        setSuccess('Contact updated.');
      } else {
        await axios.post(`${API}/company-contacts`, payload);
        setSuccess('Contact created.');
      }

      await fetchContacts();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (contact) => {
    setEditingContactId(contact.id);
    setForm({
      name: contact.name || '',
      company_name: contact.company_name || '',
      title: contact.title || '',
      phone: contact.phone || '',
      email: contact.email || '',
      notes: contact.notes || '',
    });
    setSuccess('');
    setError('');
  };

  const handleDelete = async (contact) => {
    if (!canManage) return;
    if (!window.confirm(`Delete ${contact.name}?`)) return;
    setError('');
    setSuccess('');
    try {
      await axios.delete(`${API}/company-contacts/${contact.id}`);
      setSuccess('Contact deleted.');
      await fetchContacts();
      if (editingContactId === contact.id) resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete contact');
    }
  };

  if (!canManage) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800" data-testid="company-contacts-no-access-page">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-800 mb-4" data-testid="company-contacts-back-btn">← Back to Management</button>
          <h1 className="text-2xl font-bold" data-testid="company-contacts-no-access-title">Company Contacts</h1>
          <p className="text-sm text-gray-600 mt-2" data-testid="company-contacts-no-access-message">Only Admin and Manager users can manage contacts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800" data-testid="company-contacts-page">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium" data-testid="company-contacts-back-btn">← Back to Management</button>
            <h1 className="text-3xl font-bold mt-2" data-testid="company-contacts-title">Company Contacts</h1>
            <p className="text-sm text-gray-600 mt-1" data-testid="company-contacts-subtitle">Directory for engineers, inspectors, and external contacts that can be linked to jobs.</p>
          </div>
          <button
            onClick={fetchContacts}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
            data-testid="company-contacts-refresh-btn"
          >
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <form className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4 space-y-3" onSubmit={onSubmit} data-testid="company-contacts-form">
            <h2 className="font-semibold" data-testid="company-contacts-form-title">{editingContactId ? 'Edit Contact' : 'Add Contact'}</h2>
            <div>
              <label className="text-xs text-gray-600" data-testid="company-contacts-name-label">Name</label>
              <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="company-contacts-name-input" required />
            </div>
            <div>
              <label className="text-xs text-gray-600" data-testid="company-contacts-company-label">Company</label>
              <input value={form.company_name} onChange={(e) => setForm((prev) => ({ ...prev, company_name: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="company-contacts-company-input" required />
            </div>
            <div>
              <label className="text-xs text-gray-600" data-testid="company-contacts-title-label">Role / Title</label>
              <input value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="company-contacts-title-input" />
            </div>
            <div>
              <label className="text-xs text-gray-600" data-testid="company-contacts-phone-label">Phone</label>
              <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="company-contacts-phone-input" />
            </div>
            <div>
              <label className="text-xs text-gray-600" data-testid="company-contacts-email-label">Email</label>
              <input value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="company-contacts-email-input" />
            </div>
            <div>
              <label className="text-xs text-gray-600" data-testid="company-contacts-notes-label">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} rows={3} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="company-contacts-notes-input" />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-3 py-2 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50" data-testid="company-contacts-save-btn" disabled={saving}>
                {saving ? 'Saving...' : editingContactId ? 'Update Contact' : 'Add Contact'}
              </button>
              {editingContactId ? (
                <button type="button" onClick={resetForm} className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-gray-100" data-testid="company-contacts-cancel-edit-btn">
                  Cancel
                </button>
              ) : null}
            </div>
            {error ? <p className="text-sm text-red-600" data-testid="company-contacts-error-message">{error}</p> : null}
            {success ? <p className="text-sm text-emerald-700" data-testid="company-contacts-success-message">{success}</p> : null}
          </form>

          <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-4" data-testid="company-contacts-list-panel">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <h2 className="font-semibold" data-testid="company-contacts-list-title">Directory</h2>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts"
                className="border border-gray-300 rounded px-3 py-2 text-sm"
                data-testid="company-contacts-search-input"
              />
            </div>

            {loading ? <p className="text-sm text-gray-500" data-testid="company-contacts-loading-message">Loading contacts...</p> : null}
            {!loading && filteredContacts.length === 0 ? <p className="text-sm text-gray-500" data-testid="company-contacts-empty-message">No contacts yet.</p> : null}

            <div className="space-y-3 max-h-[620px] overflow-y-auto pr-1" data-testid="company-contacts-list">
              {filteredContacts.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyContactsModule;
